import type { FlagProps } from '@atlaskit/flag';
import type { LogoProps } from '@atlaskit/logo';
import type { ExperimentalFeaturesOf } from '@atlassian/experimental-features';
import {
	type LegacyFeaturesProp,
	type withExperimentalFeatures,
} from './experiments/experimentalFeatures';
import type { ResolvedFeaturesOf } from '@atlassian/experimental-features/types';
import type { EventType, SCREEN_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { TraceIdContext } from '@atlaskit/react-ufo/experience-trace-id-context';
import type { ExternalUser, OptionData, Group, Team } from '@atlaskit/user-picker';
import type { RecaptchaResult } from '@atlassian/google-recaptcha';
import type {
	UpdateDirectAccessSettingResponse,
	UpdateOpenInviteResponse,
} from './components/ViralSettings/types';
import type { Plugins } from './plugins/types';
import type { OptionType } from './components/InviteeList/InviteeListContext';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { CollectionsContextEntitlementsQuery$data } from './experiments/twcForCoreInvites/__generated__/CollectionsContextEntitlementsQuery.graphql';

export { Integration, ScopeState } from '@atlassian/integrations/third-party';
export type {
	GenericIntegrations,
	IntegrationMetadata,
	IntegrationsResponse,
	IntegrationsScopeState,
	SlackWorkspace,
} from '@atlassian/integrations/third-party';

export interface RecommendedUserItem {
	id: string;
	name?: string;
	nickname?: string;
	email?: string;
	entityType: string;
	attributes?: Record<string, string>;
	userType?: string;
	accessLevel?: string;
	avatarUrl: string;
	description?: string;
	teamAri?: string;
	displayName?: string;
}

export type AccountInfo = {
	account_id: string;
	email: string;
};

/**
 * Represents a user that can be featured in the invite people form which is used as a list for the experimental_featuredUsers prop.
 * A featured user can be identified by either their Atlassian account ID or email.
 */
export type FeaturedUser = FeaturedUserWithId | FeaturedUserWithEmail;
export type FeaturedUserProvider = FeaturedUser[] | (() => Promise<FeaturedUser[]>);

export type FeaturedUserWithId = {
	id: string;
	name: string;
	avatarUrl: string;
	source: string;
	tooltip: string;
	email?: string;
};

export type FeaturedUserWithEmail = {
	email: string;
	source: string;
	tooltip: string;
	id?: string;
	name?: string;
	avatarUrl?: string;
};

export type InviteProcessingResponse<T = any, E = any> = (
	| {
			responseStatus: InviteProcessingResponseStatus.SUCCESS;
			response: T;
	  }
	| { responseStatus: InviteProcessingResponseStatus.ERROR; error: E }
	| { responseStatus: InviteProcessingResponseStatus.SKIPPED }
) & { flags: Flag[] };

export enum InviteProcessingResponseStatus {
	SUCCESS = 'success',
	ERROR = 'error',
	SKIPPED = 'skipped',
}

export type SelectedProduct = string;

export type InviteeListOption = OptionType;

/**
 * The Invite People package data that is exposed to consumers of it.
 */
export interface InvitePeopleData {
	groups: InvitedGroup[];
	inviteCapabilities: InviteCapabilitiesObject;
	inviteeListOptions: InviteeListOption[];
	selectedProducts: SelectedProduct[];
	users: InvitedUser[];
	teams?: InvitedTeam[];
	hasPreselectedTwc: boolean;
}

export type SubmitButton = {
	onClick: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => Promise<void>;
};

export type SubmitButtonRef = React.MutableRefObject<SubmitButton | undefined>;

export type OpenInviteResponse = PromiseSettledResult<
	InviteProcessingResponse<UpdateOpenInviteResponse, unknown>
>;

export type DirectAccessResponse = PromiseSettledResult<
	InviteProcessingResponse<UpdateDirectAccessSettingResponse, unknown>
>;
export type InviteUserResponse = PromiseSettledResult<
	InviteProcessingResponse<BulkInviteResponse, unknown>
>;
export interface OnSubmitData {
	/**
	 * Default Core Invites `onSubmit` handler.
	 */
	defaultOnSubmit: () => Promise<{
		openInviteResponse: OpenInviteResponse;
		directAccessSettingsResponse: DirectAccessResponse;
		inviteUsersResponse: InviteUserResponse;
	}>;
	/**
	 * Core Invites data at submission time.
	 */
	invitePeopleData: InvitePeopleData;
	/**
	 * Function to generate a recaptcha token.
	 */
	getRecaptchaToken?: RecaptchaResult['getRecaptchaToken'];
	/**
	 * Submit tracing context to pass across async calls
	 */
	traceContext?: TraceIdContext;
}

export interface OnInviteeLoadOptionsArguments {
	searchText: string;
}
export type OnInviteeLoadOptionsResult = (ExternalUser | Group | Team)[];
export type OnInviteeLoadOptions = (
	data: OnInviteeLoadOptionsArguments,
) => Promise<OnInviteeLoadOptionsResult>;
export type OnInviteeResultsMerged = (data: OptionData[]) => OptionData[];
export interface InviteeProvider {
	/**
	 * Callback that allows adding a list of external search results as part of the invitee list options.
	 */
	onInviteeLoadOptions: OnInviteeLoadOptions;
	/**
	 * Called once all the searches for invitees have completed and successfully merged.
	 * Such searches include `onInviteeLoadOptions` other then the usual Invite People searches.
	 *
	 * This callback allows the consumer to modify (sort, filter, etc) the invitee list options data
	 * before being rendered in the dropdown.
	 * **Please note** that this callback should not add new items and that it should be done through `onInviteeLoadOptions`
	 * since those items will be merged with other internal search results.
	 *
	 * Also, please note that the returned data of this callback will be the final list to be rendered to the user.
	 */
	onInviteeResultsMerged?: OnInviteeResultsMerged;
}
export const FREE_EDITION = 'FREE_EDITION' as const;
export const STANDARD_EDITION = 'STANDARD_EDITION' as const;
export const PREMIUM_EDITION = 'PREMIUM_EDITION' as const;
export const UNLICENSED = 'UNLICENSED' as const;

export type ApplicationEdition =
	| typeof FREE_EDITION
	| typeof STANDARD_EDITION
	| typeof PREMIUM_EDITION
	| typeof UNLICENSED;
export interface InvitePeopleProps {
	/**
	 * UserPicker's `allowEmail` prop which indicates whether the user is allowed to enter emails as a value.
	 * Default value is `true` for backwards compatibility.
	 */
	allowEmail?: boolean;
	/**
	 * UserPicker's `groupByTypeOrder` prop which group the options by type
	 */
	groupByTypeOrder?: NonNullable<OptionData['type']>[];
	/**
	 * Injects plugins within Core Invites.
	 */
	plugins?: Plugins;
	/**
	 * Callback allowing consumer to run custom logic for onSubmit and/or use the Core Invites submit handler (`defaultOnSubmit`).
	 * NOTE: when `onSubmit` is provided, if `defaultOnSubmit` is not called, then Invite People submit logic won't be called.
	 */
	onSubmit?: (data: OnSubmitData) => Promise<void>;
	/**
	 * Provides a list of external search results as part of the invitee list options.
	 */
	inviteeProvider?: InviteeProvider;
	/**
	 * Callback for when Invite List gets focused.
	 */
	onInviteeListFocus?: () => void;
	// alignment of send and cancel buttons
	alignButtons?: AlignButtons;
	// A custom label to the Cancel button
	cancelButtonLabel?: React.ReactNode;
	// The URL where the invited users will land once they join the product
	continueUrl?: string;
	hideCancelButton?: boolean;
	// A custom title to the invite form
	formTitle?: React.ReactNode;
	hideFormHeader?: boolean;
	// A custom title byline for the Invite from Link Insert v3 experiment
	experimental_formTitleByline?: string;
	// A custom user picker byline for the Invite from Link Insert v3 experiment
	experimental_userPickerByline?: string;
	// A custom description to the invite form
	formDescription?: React.ReactNode;
	// Will provide a custom label to the button Send invite
	sendButtonLabel?: React.ReactNode;
	// name of the sub-product, for jira users only
	subProduct?: SubProduct;
	// Override the list of available products to select from
	/**
	 * @deprecated Property is being removed in PUMA-415 If you need to hide
	 * the product select, use the prop `showProductSelect`.
	 * See: https://hello.atlassian.net/wiki/x/F0NxVQE
	 */
	productOptions?: ProductSelectOption[];
	// Show the product options if other products are present in site, will default to `true` if not provided.
	showProductSelect?: boolean;
	// Enable third party integration searching
	enableThirdParty?: boolean;
	/**
	 * Indicates whether the third party connect legend should be hidden, defaults to false
	 */
	hideThirdPartyConnectLegend?: boolean;
	// Disable viral settings feature
	disableViralSettings?: boolean;
	// Disable display no invite permission error message
	disableNoInviteError?: boolean;
	// Feature flags grouped in a single prop. Contents may be unstable.
	experimentalFeatures?: InvitePeopleExperimentalFeatures;
	// Callback function for showing successful or error flags
	showFlag?: GenericShowFlag<any>;
	// Show the INFI Spotlight if not seen before
	displayInfiSpotlight?: boolean;
	// Source where the invite people component is triggered
	source?: string;
	// We use this prop to test robot screen inside People menu and you should not use in production.
	_hasError?: boolean;
	// Max number of text fields (normally up to 10) on the invite form
	maxNumberOfInputs?: number;
	// Function to handle post send
	onSendHandler?: () => void;
	// Function to handle post cancel
	onCancelHandler?: () => void;
	// Function to handle post invite, it will pass through the invite api response
	onInviteCompletedHandler?: (inviteResponse: BulkInviteResponse) => void;
	// Experimental handler function to be able to reopen core invite from within the component
	experimental_onReopenHandler?: () => void;
	// Initial load of component
	isInitialLoad?: boolean;
	// Function to update `isInitialLoad`
	setIsInitialLoad?: (isInitialLoad: boolean) => void;
	// Site ARI: ari:cloud:confluence::site/<id>
	resourceAri: string;
	// Describe if the current user is an admin, a trusted user or a basic user
	userRole?: UserRole;
	// Describes the wrapper/container core invites was rendered in
	viewAs?: ViewAs;
	// id of the organisation that the current site belongs to
	orgId?: string;
	// titleId from useModal when the component is rendered in a modal for aria-dialog-name
	titleId?: string;
	// Edition of the current site. This is used for the everyoneIsAdmin Free experiment.
	experimental_applicationEdition?: ApplicationEdition;
	/**
	 * The callback for nudge to invite team inside core invites. This callback is called to update the nudge state
	 * when the nudge is shown, confirmed or dismissed.
	 */
	experimental_setFirstInviteIntentState?: (nudgeState: string) => void;
	/**
	 * The `onClick` callback triggers the onSubmit logic, as if the user had clicked on the Core Invites submit button.
	 * Passing this ref would hide the submit button and cancel button and allows for an external submit button to trigger
	 * Core Invites.
	 */
	experimental_submitButtonRef?: SubmitButtonRef;
	// Props related to navigation
	navigation?: {
		// the entry point for the PT Directory App in the product
		directoryContextEntryPoint?: string;
	};
	// Pre-fills the invitation form with a list of invitees
	initialInvitees?: InviteeListOption[];
	/**
	 * Custom text for the invitee list field label
	 */
	inviteeListLabelText?: React.ReactNode;
	/**
	 * Custom text for the invitee list field placeholder
	 */
	inviteeListPlaceholderText?: React.ReactNode;
	/**
	 * Indicates whether the invitee list is required, defaults to true
	 */
	inviteeListRequired?: boolean;
	// Force loading state (spinner) to start
	forceLoading?: boolean;
	/*
	 * Callback to handle changes to the invitee list
	 */
	experimental_onInviteeListChangeHandler?: (emails: string[]) => void;
	/**
	 * Pre-populated users displayed as clickable tags in the invite form
	 * These are externally controlled and distinct from dynamic recommendations
	 */
	experimental_featuredUsers?: FeaturedUserProvider;
	/**
	 * Additional click handlers to pass to the dropdown menu shown to admins.
	 */
	onAdminMenuItemClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
	/**
	 * experimental prop that changes the styling on the 3rd party buttons in the invite screen to be more subtle
	 */
	experimental_inlineThirdPartyButtons?: boolean;
	/**
	 * Additional contextual metadata to add context to the invite.
	 */
	experimental_productContext?: Record<string, string>;
	/**
	 * Pre-generated invite link to "Copy Link" button
	 */
	experimental_copyLink?: string | undefined;
}

export interface InvitePeopleFormProps {
	shouldRenderProductSelect?: boolean;
	productOptions?: ProductSelectOption[];
	handleProductSelectMenuOpen: () => void;
	showEmptyError: boolean;
	onCancelButtonClick?: () => void;
	maxNumberOfInputs: number;
	onInviteeListChange: (emails: string[]) => void;
	selectedProducts: string[];
	showSecondarySection: boolean;
	recaptcha: RecaptchaResult | undefined;
	setRecaptcha: (recaptcha: RecaptchaResult) => void;
}

export interface InvitePeopleFormCustomRef {
	focusInviteeList: () => void;
	focusProductSelect: () => void;
}

export type InvitePeopleExperimentalFeatures = ExperimentalFeaturesOf<
	typeof withExperimentalFeatures
> &
	LegacyFeaturesProp;

export type SubProduct = 'core' | 'software' | 'servicedesk' | 'product-discovery' | string;

export type JiraSubProduct =
	| 'jira-core'
	| 'jira-software'
	| 'jira-servicedesk'
	| 'jira-product-discovery';

export const BasicUser = 'basic';
export const AdminUser = 'admin';
export const TrustedUser = 'trusted';

export type UserRole = typeof AdminUser | typeof TrustedUser | typeof BasicUser;

export enum IneligibilityReasons {
	ERROR = 'error',
	NON_ENGLISH = 'non-english',
	NOT_ADMIN_USER = 'not-admin-user',
	EDITION_NOT_FREE = 'edition-not-free',
	NOT_HAMS = 'not-hams',
	NO_COMMERCE_INFO = 'no-commerce-info',
	NO_ELIGIBLE_USERS = 'no-eligible-users',
	CONFLUENCE_BEFORE_JIRA = 'confluence-before-jira',
}

export enum CollectionType {
	TEAMWORK_COLLECTION = 'TEAMWORK_COLLECTION',
}

export type Collections = {
	[key in CollectionType]?: {
		products: string[];
		isEnabled: boolean;
	};
};

export type AlignButtons = 'left' | 'right';

export interface GenericShowFlag<T> {
	(arg: T): T;
}

// These get passed into data prop for Option
export type ProductSelectOption = {
	label: string;
	value: string;
	group?: CollectionType;
	isReadOnly?: boolean;
};
export declare type Reason =
	| 'OFF'
	| 'FALLTHROUGH'
	| 'RULE_MATCH'
	| 'TARGET_MATCH'
	| 'INELIGIBLE'
	| 'SIMPLE_EVAL'
	| 'ERROR';
export declare type ErrorKind = 'WRONG_TYPE' | 'FLAG_NOT_FOUND' | 'VALIDATION_ERROR';
export declare type RuleId = string;
export declare type FlagExplanation = {
	kind: Reason;
	ruleId?: RuleId;
	ruleIndex?: number;
	errorKind?: ErrorKind;
};

export type CloseTrigger = 'blur' | 'cancel' | 'invite';

export type InvitePeopleModalProps = {
	onCloseHandler: (trigger?: CloseTrigger) => void;
} & Omit<InvitePeopleProps, 'onCancelHandler' | 'onSendHandler'>;

export type InvitePeopleComponentProps = Omit<
	InvitePeopleProps,
	| 'maxNumberOfInputs'
	| 'onSendHandler'
	| 'onCancelHandler'
	| 'onInviteCompletedHandler'
	| 'resourceAri'
	| 'userRole'
	| 'subProduct'
	| 'continueUrl'
	| 'source'
	| 'viewAs'
>;

export type AdminMenuGroupComponentProps = {
	onAdminMenuItemClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
};

export type ExternalUserType = 'crossSite' | 'thirdParty';

export type InvitedUser = UserWithID | UserWithEmail;
export type InvitedGroup = Pick<Group, 'id'>;
export type InvitedTeam = Pick<Team, 'id'>;

export type UserWithID = {
	id: string;
	email?: string;
};

// User with email visible or just email
export type UserWithEmail = {
	email: string;
	id?: string;
};
// TypeGuard for UserWithEmail
export const isUserWithEmail = (user: InvitedUser): user is UserWithEmail => {
	// our usage involves Array<InvitedUser> and it could be an empty array,
	// which may result user to be undefined
	// See noUncheckedIndexedAccess in Typescript for details
	if (user && (user as UserWithEmail).email) {
		return true;
	}
	return false;
};
export interface InvitedUserResult {
	results: {
		[resourceAri: string]: InviteResponseStatus;
	};
}

export type ResourceAri = string;

export type InviteResponseStatus =
	| 'INVITED'
	| 'INVITED_PENDING_APPROVAL'
	| 'USER_EXISTS'
	| 'PENDING_INVITE_EXISTS'
	| 'NOT_INVITED'
	| 'ERROR';

/**
 * Valid values are defined in IIS:
 * https://bitbucket.org/atlassian/id-invitations-service/src/master/src/main/java/com/atlassian/identity/invitations/model/EnablementTouchpoint.java
 */
export enum EnablementTouchpoint {
	CORE_INVITES = 'Core Invites',
}

export type InviteMetadata = {
	productContext?: Record<string, string>; // Additional product context to be passed to the invite API
	touchpoint?: EnablementTouchpoint;
	sourceProduct?: Product['id'];
};

export type InviteApiRequest = {
	users: InvitedUser[];
	continueUrl: string;
	resources: ResourceAri[];
	suppressInviteEmail?: boolean; // Flag to indicate whether to send or suppress invite email for both direct and pending invites
	metadata?: InviteMetadata;
};

export type InviteOptionalEmailApiRequest = InviteApiRequest & {
	cloudId: string; // required to perform same org check
	roleName?: string; // The role of which invitee will be granted
};

export interface GetCollectionsRequest {
	cloudId: string;
}

export interface InviteApiFailureResponse {
	key?: string;
	message: string;
	code?: string;
	description?: string;
	reason?: string;
	status: number;
}

export type InviteApiSuccessResponse = InvitedUser &
	InvitedUserResult & {
		errorReasons: {
			[resourceAri: string]: Pick<InviteApiFailureResponse, 'code' | 'message'>;
		};
	};

export interface BulkInviteSuccessResponse {
	invited: (InvitedUser & InvitedUserResult)[];
	accessRequested: (InvitedUser & InvitedUserResult)[];
	error: (InvitedUser & {
		error?: Pick<InviteApiFailureResponse, 'code' | 'message'>;
	})[];
}

export interface EntitlementsResponse {
	data: CollectionsContextEntitlementsQuery$data;
}

export interface BulkInviteFailureResponse {
	failure: InviteApiFailureResponse;
}

export type BulkInviteResponse = BulkInviteSuccessResponse | BulkInviteFailureResponse;

// User Network Failure error Messages
export const FAILED_FETCH_ERROR_MESSAGE = 'Failed to fetch';
export const NETWORK_ERROR_MESSAGE = 'NetworkError when attempting to fetch resource.';
export const HOST_NOT_FOUND_ERROR_MESSAGE =
	'A server with the specified hostname could not be found.';

// Errors from DirectAccess endpoints
export const INVALID_DOMAIN_ERROR_GET_MESSAGE =
	'getDesPromotionEligibleDomain.arg0.domainAri: a valid domain is required';
export const INVALID_DOMAIN_POST_ERROR_MESSAGE = 'Invalid domain';

// Error codes from /invite endpoint
export const EMAIL_BLOCKED = 'EMAIL_BLOCKED';
export const REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS =
	'REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS';
const INVALID_EMAIL = 'INVALID_EMAIL';
const MISSING_USERNAME = 'MISSING_USERNAME';
const EMPTY_USERNAME = 'EMPTY_USERNAME';
const USERNAME_MUST_BE_EMAIL = 'USERNAME_MUST_BE_EMAIL';

// Errors key and message from /invitations/invite which eventually calls /grant-access
export const ERROR_KEY_NO_DEFAULT_GROUP = 'noDefaultGroup';
export const NO_DEFAULT_GROUP_ERROR_MESSAGE = 'Default group for the requested resource not found.';

export type BadRequesetErrorCodes =
	| typeof EMAIL_BLOCKED
	| typeof INVALID_EMAIL
	| typeof MISSING_USERNAME
	| typeof EMPTY_USERNAME
	| typeof USERNAME_MUST_BE_EMAIL
	| typeof REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS;

// Partial Error type from https://aid-account.stg.internal.atlassian.com/api-explorer#/invite/invite
export type BadRequestErrorResponse = [
	{
		errors: { userName: [string] };
		errorCodes: { userName: [BadRequesetErrorCodes] };
		globalErrorCodes: [BadRequesetErrorCodes];
		errorMessages: [string];
	},
];

export interface Resource {
	resourceOwner: string;
	resourceType: string;
	resourceId: string;
}

export type PermissionId = 'invite-users' | 'manage';

export type PermsApiRequest = {
	permissionId: PermissionId;
	cloudId: string;
};
export type PermsApiResponse = {
	permitted: boolean;
};

export interface PermsApiResponseInterface {
	role: UserRole;
	isInviteUsersPermitted?: boolean;
	isManagePermitted?: boolean;
	errorMessage?: string;
}

export type Flag = Pick<FlagProps, 'appearance' | 'title' | 'description' | 'actions' | 'icon'>;

export type AnalyticsPayload = ScreenAnalyticsPayload | NonScreenAnalyticsPayload;

export type ScreenAnalyticsPayload = {
	eventType: typeof SCREEN_EVENT_TYPE;
	name: string;
	attributes?: AnalyticsAttributes;
};

export type NonScreenAnalyticsPayload = {
	eventType: Exclude<EventType, typeof SCREEN_EVENT_TYPE>;
	actionSubject: AnalyticsActionSubject;
	action: AnalyticsAction;
	actionSubjectId?: string;
	attributes?: AnalyticsAttributes;
	tags?: AnalyticsTags;
};

export type AnalyticsAttributes = Record<string, any>;

export type AnalyticsTags = string[];

export enum AnalyticsActionSubject {
	ELIGIBILITY = 'eligibility',
	INVITE_FORM = 'inviteForm',
	ERROR_BOUNDARY = 'errorBoundary',
	USER = 'user',
	PRODUCT_ACCESS = 'productAccess',
	INVITE = 'invite',
	INVITE_REQUEST = 'inviteRequest',
	SLO_INVITE_REQUEST = 'sloInviteRequest',
	SLO_THIRD_PARTY_INIT = 'sloThirdPartyInit',
	THIRD_PARTY_INIT = 'thirdPartyInit',
	LINK = 'link',
	BUTTON = 'button',
	PERMS = 'permissions',
	INVITE_CAPABILITIES = 'inviteCapabilities',
	INVITE_PEOPLE_FEATURE = 'feature',
	DROPDOWN_ITEM = 'dropdownItem',
	DROPDOWN = 'dropdown',
	INVITEE_LIST = 'inviteeList',
	INVITEE_LIST_ITEM = 'inviteeListItem',
	INVITEE_LIST_ITEM_USER_PICKER = 'inviteeListItemUserPicker',
	USER_PICKER = 'userPicker',
	CHECKBOX = 'checkbox',
	SETTINGS = 'settings',
	MODAL = 'modal',
	THIRD_PARTY_INVITES = 'thirdPartyInvites',
	THIRD_PARTY_INTEGRATION = 'thirdPartyIntegration',
	THIRD_PARTY_INTEGRATIONS = 'thirdPartyIntegrations',
	EMAIL_INPUT = 'emailInput',
	GET_USER_INVITES_SETTINGS = 'getUserInvitesSettings',
	GET_DIRECT_ACCESS_SETTING = 'getDirectAccessSetting',
	APPROVED_DOMAIN_SETTINGS = 'approvedDomainsSettings',
	USER_INVITE_SETTINGS = 'userInvitesSettings',
	SLO_GET_USER_INVITES_SETTINGS = 'sloGetUserInvitesSettings',
	SLO_GET_DIRECT_ACCESS_SETTING = 'sloGetDirectAccessSetting',
	SLO_APPROVED_DOMAIN_SETTINGS = 'sloApprovedDomainsSettings',
	SLO_USER_INVITE_SETTINGS = 'sloUserInvitesSettings',
	CORE_INVITE_COMPONENT = 'coreInviteComponent',
	USER_INVITE_SETTINGS_HISI = 'userInvitesSettingsHISI',
	SLACK_WORKSPACES = 'slackWorkspaces',
	ACCOUNT_INFO = 'accountInfo',
	FETCH_EXISTING_INTEGRATIONS = 'fetchExistingIntegrations',
	FETCH_ORG_SETTINGS = 'fetchOrgSettings',
	VALIDATION = 'validation',
	TEAM_MEMBER_PICKER = 'teamMemberPicker',
	TEAM_OR_USER_PICKER = 'teamOrUserPicker',
	SLACK_CHANNEL = 'slackChannel',
	SLACK_USER = 'slackUser',
	GET_SLACK_CHANNELS = 'getSlackChannels',
	VIEW_SLACK_TEAM_MEMBERS = 'viewSlackTeamMembers',
	NO_INVITE_PERMOISSION_SCREEN = 'noInvitePermissionScreen',
	FETCH_LINKED_ORG = 'fetchLinkedOrg',
	FETCH_OPEN_INVITE_NOTE = 'fetchOpenInviteNote',
	TRANSLATION_MESSAGE = 'translationMessage',
	INFI_SPOTLIGHT = 'infiSpotlight',
	SET_USER_AS_ADMIN = 'setUserAsAdmin',
	FETCH_COMMERCE_INFO = 'fetchCommerceInfo',
	TEAM_INVITES = 'teamInvites',
	FETCH_ELIGIBLE_USERS = 'fetchEligibleUsers',
	DRIVE_MULTI_PRODUCTS_SUCCESS_MODAL = 'driveMultiProductsSuccessModal',
	PRODUCT_CHECKBOX = 'productCheckbox',
	FEATURED_USER_TAG = 'featuredUserTag',
	FEATURED_USERS = 'featuredUsers',
	TWC_FOR_CORE_INVITES = 'twcForCoreInvites',
	GET_COLLECTIONS = 'getCollections',
}

export enum AnalyticsActionSubjectId {
	CONNECT = 'connect',
	CONTINUE_UPDATE = 'continueUpdate',
	DISCONNECT_UPDATE = 'disconnectUpdate',
	NOT_NOW_UPDATE = 'notNowUpdate',
	UPDATE_WARNING = 'updateWarning',
	FORM = 'form',
	PLUGINS = 'plugins',
	USER_MANAGEMENT = 'userManagementLink',
	ADD_YOUR_TEAM = 'addYourTeam',
	ENABLE_INVITE_LINK_CLOSE = 'enableInviteLinkCloseButton',
	ENABLE_INVITE_LINK_TOGGLE = 'enableInviteLinkToggle',
	COPY_INVITE_LINK_BUTTON = 'copyInviteLinkButton',
	SUBMIT_DRIVE_MULTI_PRODUCTS_SUCCESS_MODAL = 'submitDriveMultiProductsSuccessModal',
	EXIT_DRIVE_MULTI_PRODUCTS_SUCCESS_MODAL = 'exitDriveMultiProductsSuccessModal',
}

export enum AnalyticsAction {
	CHECKED = 'checked',
	UNCHECKED = 'unchecked',
	LOADED = 'loaded',
	CLICKED = 'clicked',
	ADDED = 'added',
	INVITED = 'invited',
	REQUESTED = 'requested',
	FAILED = 'failed',
	SUCCEEDED = 'succeeded',
	EXPOSED = 'exposed',
	OPENED = 'opened',
	AUTO_OPENED = 'autoOpened',
	CLOSED = 'closed',
	SKIPPED = 'skipped',
	CONNECTED = 'connected',
	CHANGED = 'changed',
	RENDERED = 'rendered',
	UPDATED = 'updated',
	UPDATE_FAILED = 'updateFailed',
	CREATED = 'created',
	CREATE_FAILED = 'createFailed',
	FETCHED = 'fetched',
	FETCH_FAILED = 'fetchFailed',
	VIEWED = 'viewed',
	HOVERED = 'hovered',
	TYPED = 'typed',
	SENT = 'sent',
	SELECTED = 'selected',
	EXCLUDED = 'excluded',
	PREFILLED = 'prefilled',
	DISMISSED = 'dismissed',
}

export enum AnalyticsSource {
	ADD_PEOPLE = 'addPeople',
	SLACK_CONNECT_MODAL = 'slackConnectModal',
}

interface InviteCapability {
	mode: string;
	permittedResources?: Array<string>;
	domains?: Array<string>;
}

export interface SlackUserDetails {
	id: string;
	name: string;
	realName: string;
	displayName: string;
	avatarUrl: string;
	type: string;
	email: string;
}

export interface InviteCapabilitiesEntry extends InviteCapabilitiesResponse {
	// Site ARI: ari:cloud:confluence::site/<cloud_id>
	resourceARI: string;
	// e.g. "ari:cloud:confluence::role/product/member"
	role: string;
}

export interface InviteCapabilitiesResponse {
	directInvite: InviteCapability;
	invitePendingApproval: InviteCapability;
}

export interface InviteProduct {
	name: string;
	id: string;
	ari: string;
	domains?: string[];
}
export interface Product {
	id: string;
	label: string;
	landingPath: string;
	directAccessLocation: string;
	icon: React.FC<LogoProps | any>;
}

export interface InviteCapabilitiesObject {
	directInvite: InviteProduct[];
	invitePendingApproval: InviteProduct[];
}

export type GetFlagResponse = {
	response: HaveISeenItFlag;
	cached: boolean;
};

export type HaveISeenItFlag = {
	status: boolean;
	lastSeenDate: string | null;
};

export enum Cohort {
	VARIATION = 'variation',
	EXPERIMENT = 'experiment',
	CONTROL = 'control',
	NOT_ENROLLED = 'not-enrolled',
}

export interface UserDetailsAndPerms {
	id: string;
	hasProductAccess: boolean;
	details?: SlackUserDetails;
}
export interface ExternalUserSource {
	sourceId: string;
	sourceType: 'SLACK' | 'MICROSOFT' | 'GOOGLE';
}

export type ResolvedFeatures = ResolvedFeaturesOf<typeof withExperimentalFeatures>;

export type CoreInvitesOptimisationCohort =
	| 'control'
	| 'not-enrolled'
	| 'layout-rearrangement-variation'
	| 'layout-rearrangement-not-enrolled'
	| 'layout-rearrangement-and-nth-users-product-selector-variation'
	| 'layout-rearrangement-and-nth-users-product-selector-not-enrolled';

/*
https://atlaskit.atlassian.com/packages/design-system/theme/docs/constants
*/
export enum ZIndex {
	MODAL = 510,
	DRAWER = 550,
	FLOATING = 560,
}

export type ViewAs = 'modal' | 'drawer' | 'unknown';

export type CommerceService = 'HAMS' | 'CCP';

export type FetchCommerceRolloutInformationResponse = {
	service: CommerceService;
};

export enum ExperimentKeys {
	SLACK_AUTO_CONNECT = 'core_invites_auto_slack_select',
	FEATURED_USERS = 'recommend_teammates_in_project_invite',
	SLACK_RECOMMENDATIONS = 'project_invite_3p_slack_recommendations',
}
